<script setup lang="ts">
import { inject, Ref } from 'vue'
import { computed } from 'vue-demi'
import { useI18n } from 'vue-i18n'
import {
  VyzvaSpecificationPublicResponse,
  ZoppmBodoveKriteriumSpecRef,
} from '/@src/client-public'
import { useSteps } from '/@src/composable/useSteps'
import { newLineToLineBreak } from '/@src/utils/format'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface BodoveKriteria {
  bodoveKriteria: any
  isValidSection: boolean
}

const props = withDefaults(defineProps<BodoveKriteria>(), {
  bodoveKriteria: undefined,
  isValidSection: false,
})

const { t } = useI18n()
const steps = useSteps()
const specResponse = inject<VyzvaSpecificationPublicResponse>('specResponse')
const stavPodana = inject<boolean>('stavPodana', false)

const bodoveKriteriaData = computed(() => props?.bodoveKriteria)

function getBodoveKriterium(
  zoppmBodoveKriteriumSpecId: ZoppmBodoveKriteriumSpecRef['zoppmBodoveKriteriumSpecId']
) {
  return specResponse?.zoppmSpec?.bodoveKriteria?.find(
    (kriterium) =>
      kriterium?.zoppmBodoveKriteriumSpecRef?.zoppmBodoveKriteriumSpecId?.toString() ===
      zoppmBodoveKriteriumSpecId?.toString()
  )
}

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ks.kriteria'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.bodoveKriteria.mainHeading')
)
</script>

<template>
  <IDSKHeading as="h2" variant="l" class="mb-7">
    {{ heading }}
  </IDSKHeading>

  <ZhrnutieError
    v-if="
      (!bodoveKriteriaData?.some((kriterium) => kriterium?.pocetBodov) &&
        bodoveKriteriaData?.length !== 0) ||
      isValidSection
    "
  ></ZhrnutieError>

  <template v-for="(kriterium, key) in bodoveKriteriaData" :key="key">
    <IDSKHeading as="h3" variant="m" class="mb-6">
      {{ key + 1 }}. {{ kriterium.zoppmBodoveKriteriumSpecRef.nazov }}
    </IDSKHeading>
    <IDSKParagraph
      v-if="
        getBodoveKriterium(
          kriterium?.zoppmBodoveKriteriumSpecRef?.zoppmBodoveKriteriumSpecId
        )?.popis
      "
      class="mb-6"
    >
      {{
        getBodoveKriterium(
          kriterium?.zoppmBodoveKriteriumSpecRef?.zoppmBodoveKriteriumSpecId
        )?.popis
      }}
    </IDSKParagraph>

    <template
      v-if="
        getBodoveKriterium(
          kriterium?.zoppmBodoveKriteriumSpecRef?.zoppmBodoveKriteriumSpecId
        )
      "
    >
      <template
        v-for="(metodika, idx) in getBodoveKriterium(
          kriterium?.zoppmBodoveKriteriumSpecRef?.zoppmBodoveKriteriumSpecId
        )?.metodika"
        :key="idx"
      >
        <IDSKHeading v-if="metodika?.nazov" as="h4" variant="s" class="mb-2">
          {{ metodika?.nazov }}
        </IDSKHeading>
        <IDSKParagraph v-if="metodika?.popis" class="mb-6">
          {{ metodika?.popis }}
        </IDSKParagraph>
      </template>
    </template>

    <div class="mb-6">
      <IDSKHeading v-if="kriterium?.pocetBodov" as="h4" variant="s" class="mb-1 pt-0">
        <b>{{ t('zoppm.zhrnutie.bodoveKriteria.pointsNum') }}:</b>
      </IDSKHeading>
      <IDSKParagraph v-if="kriterium?.pocetBodov" class="mb-6">
        {{ kriterium.pocetBodov }}
      </IDSKParagraph>

      <IDSKHeading v-if="kriterium?.poznamka" as="h4" variant="s" class="mb-1 pt-0">
        <b>{{ t('zoppm.zhrnutie.bodoveKriteria.poznamka') }}:</b>
      </IDSKHeading>
      <IDSKParagraph
        v-if="kriterium?.poznamka"
        v-html="newLineToLineBreak(kriterium?.poznamka)"
      />
    </div>
  </template>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-bodove-kriteria-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['bodoveKriteria'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>
